import type { Label, PresetParam, PresetParams } from './presets';
import { DIRECTIONS, type Direction } from './presets';
import type { CssKeyframe } from './pose';

/**
 * Scene transitions. They move whole scenes in canvas space, so they animate the individual
 * `translate` and `scale` properties and an additive `filter`, which compose with the layer's own
 * enter/exit animations (those use `transform`, `opacity` and their own filter) instead of
 * fighting them. Wipes clip both scenes along the same moving edge, so they work whatever the
 * layer order is.
 */

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TransitionContext {
  canvas: { width: number; height: number };
  /** The layer's box in canvas coordinates (unrotated). */
  box: Box;
  params: PresetParams;
}

export interface TransitionFrames {
  /** Keyframes for the layers of the scene that leaves. */
  out: CssKeyframe[];
  /** Keyframes for the layers of the scene that comes in. */
  in: CssKeyframe[];
  /** `add` for filters (so they stack on the layer's own), otherwise replace. */
  composite?: 'add' | 'replace';
}

export interface TransitionPreset {
  id: string;
  label: Label;
  duration: number;
  easing: string;
  params?: PresetParam[];
  frames: (ctx: TransitionContext) => TransitionFrames;
  /**
   * Motion graphics over the cut (see fx.ts), spanning the whole transition; the transition's
   * own params (direction…) are passed on. Cover effects hide the canvas around the midpoint,
   * where `swap` frames switch scenes.
   */
  cover?: { kind: string; params?: PresetParams };
}

const P_DIRECTION: PresetParam = {
  key: 'direction',
  type: 'direction',
  default: 'left',
  label: ['Direction', 'Напрямок', 'Kierunek', 'Direcție', 'Dirección', 'Направление'],
};

const dirOf = (p: PresetParams, d: Direction = 'left'): Direction =>
  typeof p.direction === 'string' && (DIRECTIONS as string[]).includes(p.direction)
    ? (p.direction as Direction)
    : d;

/** Canvas-sized offset in the direction the content travels. */
function travel(d: Direction, canvas: { width: number; height: number }) {
  switch (d) {
    case 'left':
      return { x: -canvas.width, y: 0 };
    case 'right':
      return { x: canvas.width, y: 0 };
    case 'up':
      return { x: 0, y: -canvas.height };
    case 'down':
      return { x: 0, y: canvas.height };
  }
}

const px = (v: { x: number; y: number }, k = 1) =>
  `${Math.round(v.x * k * 100) / 100}px ${Math.round(v.y * k * 100) / 100}px`;

/** Scale around the canvas centre, expressed as translate + scale of the layer itself. */
function zoom(ctx: TransitionContext, k: number): CssKeyframe {
  const cx = ctx.canvas.width / 2;
  const cy = ctx.canvas.height / 2;
  const ex = ctx.box.x + ctx.box.width / 2;
  const ey = ctx.box.y + ctx.box.height / 2;
  return { translate: px({ x: (k - 1) * (ex - cx), y: (k - 1) * (ey - cy) }), scale: String(k) };
}

/**
 * Inset clip (in % of the layer box) that keeps the part of the layer on one side of a
 * canvas-space edge. `edge` is the position of the edge along the travel axis, 0..1 of the canvas.
 */
function edgeClip(
  ctx: TransitionContext,
  d: Direction,
  edge: number,
  keep: 'before' | 'after',
): string {
  const { box, canvas } = ctx;
  const pad = -50;
  const pct = (v: number, size: number) => Math.round((v / size) * 10000) / 100;
  const clip = { top: pad, right: pad, bottom: pad, left: pad };
  const horizontal = d === 'left' || d === 'right';
  const size = horizontal ? box.width : box.height;
  const start = horizontal ? box.x : box.y;
  // Edge in canvas px; for left/up wipes the edge travels from the far side
  const pos =
    (d === 'right' || d === 'down' ? edge : 1 - edge) * (horizontal ? canvas.width : canvas.height);
  const local = pos - start; // edge in the layer's px
  // "before" = the side the edge has already passed
  const passedIsStart = d === 'right' || d === 'down';
  const hideStart = keep === 'before' ? !passedIsStart : passedIsStart;
  if (hideStart) {
    const v = pct(local, size);
    if (horizontal) clip.left = v;
    else clip.top = v;
  } else {
    const v = pct(size - local, size);
    if (horizontal) clip.right = v;
    else clip.bottom = v;
  }
  return `inset(${clip.top}% ${clip.right}% ${clip.bottom}% ${clip.left}%)`;
}

/**
 * Hard switch at the midpoint, hidden under a cover effect; the incoming scene settles in with a
 * small move in the direction of travel. Keyframes carry their own easing (the timing is linear
 * so the midpoint stays in sync with the cover).
 */
function swap(ctx: TransitionContext, d?: Direction, k = 0.06): TransitionFrames {
  const t = d ? travel(d, ctx.canvas) : { x: 0, y: 0 };
  const settle = 'cubic-bezier(0.16, 1, 0.3, 1)';
  return {
    out: [
      { filter: 'opacity(1)', offset: 0 },
      { filter: 'opacity(1)', offset: 0.5 },
      { filter: 'opacity(0)', offset: 0.5 },
      { filter: 'opacity(0)', offset: 1 },
    ],
    in: [
      { filter: 'opacity(0)', translate: px(t, -k), offset: 0 },
      { filter: 'opacity(0)', translate: px(t, -k), offset: 0.5 },
      { filter: 'opacity(1)', translate: px(t, -k), offset: 0.5, easing: settle },
      { filter: 'opacity(1)', translate: '0px 0px', offset: 1 },
    ],
    composite: 'add',
  };
}

const P_COVER_DIRECTION: PresetParam = { ...P_DIRECTION, default: 'right' };

export const TRANSITIONS: TransitionPreset[] = [
  {
    id: 'cut',
    label: ['Cut', 'Склейка', 'Cięcie', 'Tăietură', 'Corte', 'Склейка'],
    duration: 16,
    easing: 'linear',
    frames: () => ({ out: [], in: [] }),
  },
  {
    id: 'crossfade',
    label: ['Crossfade', 'Перехрестя', 'Przenikanie', 'Fondu', 'Fundido cruzado', 'Перекрёстное'],
    duration: 600,
    easing: 'ease-in-out',
    frames: () => ({
      out: [{ filter: 'opacity(1)' }, { filter: 'opacity(0)' }],
      in: [{ filter: 'opacity(0)' }, { filter: 'opacity(1)' }],
      composite: 'add',
    }),
  },
  {
    id: 'fade-through',
    label: [
      'Fade through',
      'Через затемнення',
      'Przez wygaszenie',
      'Prin estompare',
      'Fundido a través',
      'Через затемнение',
    ],
    duration: 800,
    easing: 'ease-in-out',
    frames: () => ({
      out: [
        { filter: 'opacity(1)' },
        { filter: 'opacity(0)', offset: 0.5 },
        { filter: 'opacity(0)' },
      ],
      in: [
        { filter: 'opacity(0)' },
        { filter: 'opacity(0)', offset: 0.5 },
        { filter: 'opacity(1)' },
      ],
      composite: 'add',
    }),
  },
  {
    id: 'push',
    label: ['Push', 'Поштовх', 'Wypchnięcie', 'Împingere', 'Empujar', 'Толчок'],
    duration: 700,
    easing: 'in-out-expo',
    params: [P_DIRECTION],
    frames: (ctx) => {
      const t = travel(dirOf(ctx.params), ctx.canvas);
      return {
        out: [{ translate: '0px 0px' }, { translate: px(t) }],
        in: [{ translate: px(t, -1) }, { translate: '0px 0px' }],
      };
    },
  },
  {
    id: 'slide-over',
    label: ['Slide over', 'Наїзд', 'Najazd', 'Suprapunere', 'Superponer', 'Наезд'],
    duration: 700,
    easing: 'out-expo',
    params: [P_DIRECTION],
    frames: (ctx) => {
      const t = travel(dirOf(ctx.params), ctx.canvas);
      return {
        out: [
          { translate: '0px 0px', filter: 'brightness(1)' },
          { translate: px(t, 0.25), filter: 'brightness(0.5)' },
        ],
        in: [
          { translate: px(t, -1), filter: 'brightness(1)' },
          { translate: '0px 0px', filter: 'brightness(1)' },
        ],
        composite: 'add',
      };
    },
  },
  {
    id: 'whip-pan',
    label: [
      'Whip pan',
      'Швидка панорама',
      'Szybka panorama',
      'Panoramare rapidă',
      'Barrido',
      'Быстрая панорама',
    ],
    duration: 500,
    easing: 'in-out-expo',
    params: [P_DIRECTION],
    cover: { kind: 'lines', params: { count: 22, opacity: 0.7 } },
    frames: (ctx) => {
      const d = dirOf(ctx.params);
      const t = travel(d, ctx.canvas);
      const blur = Math.round(Math.max(ctx.canvas.width, ctx.canvas.height) * 0.03);
      return {
        out: [
          { translate: '0px 0px', filter: 'blur(0px)' },
          { translate: px(t), filter: `blur(${blur}px)` },
        ],
        in: [
          { translate: px(t, -1), filter: `blur(${blur}px)` },
          { translate: '0px 0px', filter: 'blur(0px)' },
        ],
        composite: 'add',
      };
    },
  },
  {
    id: 'zoom-in',
    label: ['Zoom through', 'Проліт', 'Przelot', 'Zbor prin', 'Zoom a través', 'Пролёт'],
    duration: 700,
    easing: 'in-out-expo',
    frames: (ctx) => ({
      out: [
        { ...zoom(ctx, 1), filter: 'opacity(1)' },
        { ...zoom(ctx, 1.6), filter: 'opacity(0)' },
      ],
      in: [
        { ...zoom(ctx, 0.7), filter: 'opacity(0)' },
        { ...zoom(ctx, 1), filter: 'opacity(1)' },
      ],
      composite: 'add',
    }),
  },
  {
    id: 'zoom-out',
    label: ['Zoom back', 'Відліт', 'Odlot', 'Zbor înapoi', 'Zoom atrás', 'Отлёт'],
    duration: 700,
    easing: 'in-out-expo',
    frames: (ctx) => ({
      out: [
        { ...zoom(ctx, 1), filter: 'opacity(1)' },
        { ...zoom(ctx, 0.6), filter: 'opacity(0)' },
      ],
      in: [
        { ...zoom(ctx, 1.5), filter: 'opacity(0)' },
        { ...zoom(ctx, 1), filter: 'opacity(1)' },
      ],
      composite: 'add',
    }),
  },
  {
    id: 'blur',
    label: ['Blur', 'Розмиття', 'Rozmycie', 'Estompare', 'Desenfoque', 'Размытие'],
    duration: 800,
    easing: 'ease-in-out',
    frames: (ctx) => {
      const b = Math.round(Math.max(ctx.canvas.width, ctx.canvas.height) * 0.025);
      return {
        out: [{ filter: 'blur(0px) opacity(1)' }, { filter: `blur(${b}px) opacity(0)` }],
        in: [{ filter: `blur(${b}px) opacity(0)` }, { filter: 'blur(0px) opacity(1)' }],
        composite: 'add',
      };
    },
  },
  {
    id: 'wipe',
    label: ['Wipe', 'Шторка', 'Kurtyna', 'Cortină', 'Cortinilla', 'Шторка'],
    duration: 800,
    easing: 'in-out-quart',
    params: [{ ...P_DIRECTION, default: 'right' }],
    frames: (ctx) => {
      const d = dirOf(ctx.params, 'right');
      return {
        out: [
          { clipPath: edgeClip(ctx, d, 0, 'after') },
          { clipPath: edgeClip(ctx, d, 1, 'after') },
        ],
        in: [
          { clipPath: edgeClip(ctx, d, 0, 'before') },
          { clipPath: edgeClip(ctx, d, 1, 'before') },
        ],
      };
    },
  },
  {
    id: 'iris',
    label: ['Iris', 'Діафрагма', 'Przesłona', 'Diafragmă', 'Iris', 'Диафрагма'],
    duration: 900,
    easing: 'in-out-quart',
    frames: (ctx) => {
      const { box, canvas } = ctx;
      // Circle centred on the canvas, in the layer's own coordinates
      const cx = canvas.width / 2 - box.x;
      const cy = canvas.height / 2 - box.y;
      const r = Math.ceil(Math.hypot(canvas.width, canvas.height) / 2) + 2;
      return {
        out: [
          { filter: 'opacity(1)', offset: 0 },
          { filter: 'opacity(1)', offset: 0.6 },
          { filter: 'opacity(0)', offset: 1 },
        ],
        in: [
          { clipPath: `circle(0px at ${Math.round(cx)}px ${Math.round(cy)}px)` },
          { clipPath: `circle(${r}px at ${Math.round(cx)}px ${Math.round(cy)}px)` },
        ],
        composite: 'add',
      };
    },
  },
  {
    id: 'stack',
    label: ['Card stack', 'Колода', 'Talia kart', 'Teanc', 'Baraja', 'Колода'],
    duration: 800,
    easing: 'out-expo',
    params: [{ ...P_DIRECTION, default: 'up' }],
    frames: (ctx) => {
      const t = travel(dirOf(ctx.params, 'up'), ctx.canvas);
      return {
        out: [
          { ...zoom(ctx, 1), filter: 'brightness(1)' },
          { ...zoom(ctx, 0.88), filter: 'brightness(0.45)' },
        ],
        in: [{ translate: px(t, -1) }, { translate: '0px 0px' }],
        composite: 'add',
      };
    },
  },
  {
    id: 'glitch',
    label: ['Glitch', 'Глітч', 'Glitch', 'Glitch', 'Glitch', 'Глитч'],
    duration: 500,
    easing: 'linear',
    cover: { kind: 'glitch' },
    frames: (ctx) => {
      const j = Math.max(2, Math.round(ctx.canvas.width * 0.02));
      const o = (x: number, y = 0) => `${x * j}px ${y * j}px`;
      return {
        out: [
          { translate: o(0), filter: 'opacity(1) brightness(1)' },
          { translate: o(-1), filter: 'opacity(0.6) brightness(1.8)' },
          { translate: o(1, 0.5), filter: 'opacity(1) brightness(1.2)' },
          { translate: o(-2), filter: 'opacity(0) brightness(2)' },
        ],
        in: [
          { translate: o(2), filter: 'opacity(0) brightness(2)' },
          { translate: o(-1, -0.5), filter: 'opacity(1) brightness(1.4)' },
          { translate: o(1), filter: 'opacity(0.5) brightness(1.8)' },
          { translate: o(0), filter: 'opacity(1) brightness(1)' },
        ],
        composite: 'add',
      };
    },
  },
  {
    id: 'panels',
    label: [
      'Colour panels',
      'Кольорові панелі',
      'Kolorowe panele',
      'Panouri colorate',
      'Paneles de color',
      'Цветные панели',
    ],
    duration: 1100,
    easing: 'linear',
    params: [P_COVER_DIRECTION],
    cover: { kind: 'panels' },
    frames: (ctx) => swap(ctx, dirOf(ctx.params, 'right')),
  },
  {
    id: 'diagonal',
    label: ['Diagonal sweep', 'Діагональ', 'Ukos', 'Diagonală', 'Diagonal', 'Диагональ'],
    duration: 1000,
    easing: 'linear',
    params: [P_COVER_DIRECTION],
    cover: { kind: 'diagonal' },
    frames: (ctx) => swap(ctx, dirOf(ctx.params, 'right') === 'left' ? 'left' : 'right'),
  },
  {
    id: 'shutter',
    label: ['Shutter', 'Жалюзі', 'Żaluzje', 'Jaluzele', 'Persiana', 'Жалюзи'],
    duration: 1100,
    easing: 'linear',
    params: [{ ...P_DIRECTION, default: 'down' }],
    cover: { kind: 'shutter' },
    frames: (ctx) => swap(ctx, dirOf(ctx.params, 'down'), 0.04),
  },
  {
    id: 'slices',
    label: ['Slices', 'Смуги', 'Paski', 'Felii', 'Franjas', 'Полосы'],
    duration: 1000,
    easing: 'linear',
    cover: { kind: 'slices' },
    frames: (ctx) => swap(ctx),
  },
  {
    id: 'stripes',
    label: [
      'Diagonal stripes',
      'Діагональні смуги',
      'Ukośne paski',
      'Dungi diagonale',
      'Rayas diagonales',
      'Диагональные полосы',
    ],
    duration: 1200,
    easing: 'linear',
    cover: { kind: 'stripes' },
    frames: (ctx) => swap(ctx),
  },
  {
    id: 'doors',
    label: ['Doors', 'Двері', 'Drzwi', 'Uși', 'Puertas', 'Двери'],
    duration: 1100,
    easing: 'linear',
    cover: { kind: 'doors' },
    frames: (ctx) => {
      const f = swap(ctx);
      // The new scene opens with the doors: a slight zoom out from behind them
      f.in = [
        { ...f.in[0], ...zoom(ctx, 1.08) },
        { ...f.in[1], ...zoom(ctx, 1.08) },
        { ...f.in[2], ...zoom(ctx, 1.08), easing: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        { ...f.in[3], ...zoom(ctx, 1) },
      ];
      return f;
    },
  },
  {
    id: 'circle',
    label: ['Circle cover', 'Коло', 'Koło', 'Cerc', 'Círculo', 'Круг'],
    duration: 1100,
    easing: 'linear',
    cover: { kind: 'circle' },
    frames: (ctx) => {
      const f = swap(ctx);
      f.in = [
        { ...f.in[0], ...zoom(ctx, 0.92) },
        { ...f.in[1], ...zoom(ctx, 0.92) },
        { ...f.in[2], ...zoom(ctx, 0.92), easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
        { ...f.in[3], ...zoom(ctx, 1) },
      ];
      return f;
    },
  },
  {
    id: 'flash',
    label: ['Flash cut', 'Спалах', 'Błysk', 'Bliț', 'Destello', 'Вспышка'],
    duration: 600,
    easing: 'linear',
    cover: { kind: 'flash' },
    frames: (ctx) => {
      const settle = 'cubic-bezier(0.16, 1, 0.3, 1)';
      return {
        out: [
          {
            ...zoom(ctx, 1),
            filter: 'opacity(1)',
            offset: 0,
            easing: 'cubic-bezier(0.7, 0, 0.84, 0)',
          },
          { ...zoom(ctx, 1.12), filter: 'opacity(1)', offset: 0.35 },
          { ...zoom(ctx, 1.12), filter: 'opacity(0)', offset: 0.35 },
          { ...zoom(ctx, 1.12), filter: 'opacity(0)', offset: 1 },
        ],
        in: [
          { ...zoom(ctx, 1.12), filter: 'opacity(0)', offset: 0 },
          { ...zoom(ctx, 1.12), filter: 'opacity(0)', offset: 0.35 },
          { ...zoom(ctx, 1.12), filter: 'opacity(1)', offset: 0.35, easing: settle },
          { ...zoom(ctx, 1), filter: 'opacity(1)', offset: 1 },
        ],
        composite: 'add',
      };
    },
  },
];

export const TRANSITION_MAP = new Map(TRANSITIONS.map((t) => [t.id, t]));
